import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { topLevelItems, visibleMenuGroups } from "./sidebar";

const ALL_ITEMS = [
  ...topLevelItems.map((item) => ({ ...item, groupName: "" })),
  ...visibleMenuGroups.flatMap((group) => group.items.map((item) => ({ ...item, groupName: group.groupName }))),
];

/**
 * Global "jump to any menu option" shortcut — Cmd/Ctrl+K opens a searchable list of every
 * sidebar item; selecting one navigates straight to that page.
 */
const CommandPalette = ({ setActivePage, open, setOpen }) => {
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      const isShortcut = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (isShortcut) {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlighted(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_ITEMS;
    return ALL_ITEMS.filter(
      (item) => item.featureName.toLowerCase().includes(q) || item.groupName.toLowerCase().includes(q)
    );
  }, [query]);

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  const select = (item) => {
    if (!item) return;
    setActivePage(item.featureKey);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(results[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && setOpen(false)}
      style={{ alignItems: "flex-start", paddingTop: "10vh" }}
    >
      <div className="modal-box" style={{ width: "560px", maxWidth: "90vw", padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "14px 16px", borderBottom: "1px solid var(--color-border)" }}>
          <Search size={16} style={{ color: "var(--color-text-faint)", flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search any menu option..."
            style={{ border: "none", outline: "none", flex: 1, fontSize: "15px", background: "transparent" }}
          />
          <kbd style={{ fontSize: "11px", color: "var(--color-text-faint)", border: "1px solid var(--color-border)", borderRadius: "4px", padding: "2px 6px" }}>
            Esc
          </kbd>
        </div>
        <div style={{ maxHeight: "360px", overflowY: "auto", padding: "6px" }}>
          {results.length === 0 ? (
            <div className="empty-state" style={{ padding: "24px" }}>
              <span className="empty-state-title">No matching menu option</span>
            </div>
          ) : (
            results.map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <button
                  key={item.featureKey}
                  type="button"
                  onClick={() => select(item)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`sidebar-subitem${index === highlighted ? " active" : ""}`}
                  style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: "10px", padding: "9px 10px" }}
                >
                  <ItemIcon size={14} />
                  <span style={{ flex: 1 }}>{item.featureName}</span>
                  {item.groupName ? (
                    <span style={{ fontSize: "12px", color: "var(--color-text-faint)" }}>{item.groupName}</span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
